import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useConnection } from "../hooks/useConnection";
import {
  getCachedCategories,
  invalidateCategoriesCache,
  loadCategoriesCatalog,
} from "../services/Categories/CategoriesCatalog";

const CategoriesContext = createContext(null);

export function CategoriesProvider({ children }) {
  const connection = useConnection();
  const cached = getCachedCategories();
  const isMountedRef = useRef(true);

  const [categories, setCategories] = useState(() => cached ?? []);
  const [loading, setLoading] = useState(() => !cached);
  const [error, setError] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (force) {
        invalidateCategoriesCache();
      }

      const currentCache = getCachedCategories();
      if (currentCache && !force) {
        if (isMountedRef.current) {
          setCategories(currentCache);
          setError(null);
          setLoading(false);
        }
        return currentCache;
      }

      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        const items = await loadCategoriesCatalog(connection);
        if (isMountedRef.current) {
          setCategories(items);
          setError(null);
        }
        return items;
      } catch (err) {
        if (isMountedRef.current) {
          setCategories([]);
          setError(err);
        }
        throw err;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [connection],
  );

  const ensureLoaded = useCallback(() => {
    load().catch((err) => {
      console.error("Error al traer las categorías:", err);
    });
  }, [load]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  const value = useMemo(
    () => ({
      categories,
      loading,
      error,
      refresh,
      ensureLoaded,
    }),
    [categories, error, ensureLoaded, loading, refresh],
  );

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories() {
  const context = useContext(CategoriesContext);

  if (!context) {
    throw new Error("useCategories debe usarse dentro de CategoriesProvider");
  }

  const { ensureLoaded } = context;

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  return context;
}
